import videojs from 'video.js'
import { v4 as uuid } from 'uuid'
import { Event } from 'video.js/dist/types/event-target'
import Player from 'video.js/dist/types/player'
import PL from 'video.js/dist/types/plugin'
import TextTrack from 'video.js/dist/types/tracks/text-track'

import trackEventsBQ from '~/analytics/analyticsBQ'
import { range } from '~/utils'

const Plugin = videojs.getPlugin('plugin') as typeof PL

enum VideoEvent {
  PLAY = 'play',
  PAUSE = 'pause',
  ENDED = 'ended',
  TIMEUPDATE = 'timeupdate',
  SEEKING = 'seeking',
  SEEKED = 'seeked',
  LOADMETADATA = 'loadedmetadata',
  FORWARD = 'forward',
  BACKWARD = 'reverse',
}

interface State {
  timestamp: number
  prevTime: number
  duration: number
  seeking: boolean
  seekStart: number
  lastEvent: VideoEvent
  viewId: string
  lockProgressUpdate: boolean
}
class VideoAnalyticsPlugin extends Plugin {
  private static VERSION = '0.0.1'
  public declare state: State
  public declare setState: (state: Partial<State> | (() => Partial<State>)) => void
  private videoCategory?: string
  // every 15 seconds of video progress
  private PROGRESS_THRESHOLD = 15
  private thresholds: Array<{ thresholdIndex: number; threshold: number }> = []
  public constructor(player: Player, options?: { videoCategory: string }) {
    super(player)
    this.setState({
      prevTime: 0,
      timestamp: 0,
      duration: 0,
      viewId: uuid(),
      seeking: false,
      seekStart: 0,
      lastEvent: VideoEvent.LOADMETADATA,
      lockProgressUpdate: false,
    })

    this.videoCategory = options?.videoCategory

    this.player.on(VideoEvent.LOADMETADATA, this.handleLoadedMetadata)
    this.player.on(VideoEvent.TIMEUPDATE, this.handleTimeUpdate)
    this.player.on([VideoEvent.PLAY, VideoEvent.PAUSE, VideoEvent.ENDED], this.handleVideoEvent)
    this.player.on(VideoEvent.SEEKED, this.handleSeeked)
    this.player.on(VideoEvent.SEEKING, this.handleSeeking)
  }

  private handleSeeked = (): void => {
    const seekType = this.state.seekStart < this.state.timestamp ? VideoEvent.FORWARD : VideoEvent.BACKWARD
    const eventParams = this.getEventParams()
    const offset = Math.abs(this.state.seekStart - this.state.timestamp)

    trackEventsBQ({ event: seekType, product: this.videoCategory, offset, ...eventParams })
    this.setState({ seeking: false, seekStart: 0, lastEvent: VideoEvent.SEEKED })
  }

  private handleSeeking = (): void => {
    this.setState({ seeking: true, seekStart: this.state.prevTime, lastEvent: VideoEvent.SEEKING })
  }

  private setUpAutoProgressTracking(videoLength: number): void {
    const totalProgressThresholds = Math.floor(videoLength / this.PROGRESS_THRESHOLD)

    this.thresholds = range(totalProgressThresholds).map((index) => {
      const thresholdIndex = index + 1
      const time = thresholdIndex * this.PROGRESS_THRESHOLD
      return { threshold: time, thresholdIndex }
    })
  }

  private handleLoadedMetadata = (): void => {
    const totalLength = this.player.duration() || 0

    this.setState({
      duration: totalLength,
      lastEvent: VideoEvent.LOADMETADATA,
      timestamp: this.player.currentTime() || 0,
    })

    if (totalLength > 0) {
      this.setUpAutoProgressTracking(totalLength)
    }
  }

  private handleTimeUpdate = (): void => {
    const timestamp = this.player.currentTime() || 0
    const prevTime = this.state.timestamp || 0

    this.setState({ timestamp, prevTime })

    this.handleAutoProgressTracking(timestamp)
  }

  private handleAutoProgressTracking(videoTimeStamp: number): void {
    if (this.state.lockProgressUpdate) {
      return
    }

    const threshold = this.thresholds.find((th) => {
      return Math.trunc(videoTimeStamp) === th.threshold || Math.trunc(videoTimeStamp) - 1 === th.threshold
    })

    if (threshold) {
      this.setState({ lockProgressUpdate: true })
      const eventParams = this.getEventParams()

      trackEventsBQ({
        event: 'progress',
        product: this.videoCategory,
        thresholdIndex: threshold.thresholdIndex,
        ...eventParams,
      })
      this.setState({ lockProgressUpdate: false })

      // eslint-disable-next-line fp/no-mutating-methods
      this.thresholds = this.thresholds.filter((th) => th.thresholdIndex !== threshold.thresholdIndex)
    }
  }

  private handleVideoEvent = (event: Event): void => {
    const isNotSeeking = !this.player.scrubbing() && !this.state.seeking
    const timeStamp = Math.floor(this.state.timestamp)
    const prevTime = Math.floor(this.state.prevTime)
    const duration = Math.floor(this.state.duration)

    /**
     * When seeking with seekbar, the player goes through pause -> seeking -> seeked -> play lifecycle
     * we want to ignore all events except seeked to reduce noise
     */
    const isSeekingLifeCycleEvent =
      timeStamp !== prevTime || (event.type === VideoEvent.PLAY && this.state.lastEvent === VideoEvent.SEEKED)

    // when the video ends pause event is fired , we don't need it either
    const isVideoEndPauseEvent = event.type === VideoEvent.PAUSE && timeStamp === duration

    if (isNotSeeking && !isSeekingLifeCycleEvent && !isVideoEndPauseEvent) {
      const eventParams = this.getEventParams()
      const eventName = event.type === VideoEvent.ENDED ? 'complete' : event.type

      trackEventsBQ({ event: eventName, product: this.videoCategory, ...eventParams })
      this.setState({ lastEvent: event.type as VideoEvent })
    }

    if (event.type === VideoEvent.ENDED) {
      this.resetViewState()
    }
  }

  private resetViewState = (): void => {
    this.setState({ viewId: uuid() })
    this.setUpAutoProgressTracking(this.state.duration)
  }

  private getEventParams = (): Record<string, unknown> => {
    const title = window.document.title?.split('|')[0]?.trim()
    const path = this.player.currentSrc()
    const speed = this.player.playbackRate()
    const language = this.player.language()
    const fullScreen = this.player.isFullscreen()
    const height = this.player.videoHeight()
    const width = this.player.videoWidth()
    const muted = this.player.muted()
    const textTrack: TextTrack | null = this.getActiveTextTrack()
    // @ts-expect-error: language is a property of TextTrack
    const caption_language = textTrack?.language
    const viewId = this.state.viewId
    const played = this.getPlayedRanges()
    const isPip = this.player.isInPictureInPicture()

    return {
      timestamp: this.state.timestamp,
      duration: this.state.duration,
      title,
      path,
      speed,
      language,
      full_screen: fullScreen,
      height,
      width,
      view_id: viewId,
      is_pip: isPip,
      muted,
      played_ranges: played,
      caption_language,
    }
  }

  private getActiveTextTrack = (): TextTrack | null => {
    const textTracks = this.player.textTracks() as unknown as TextTrack[]
    return (
      Array.from({ length: textTracks.length }, (_, index) => {
        return textTracks[index]
        // @ts-expect-error: mode is a property of TextTrack
      }).find((track) => track.mode === 'showing') || null
    )
  }

  private getPlayedRanges = (): number[][] => {
    const playedRangesCount = this.player.played()?.length

    const playedRanges = Array.from({ length: playedRangesCount }).map((_, index) => [
      this.player.played().start(index),
      this.player.played().end(index),
    ])

    return playedRanges
  }
}

videojs.registerPlugin('videoAnalytics', VideoAnalyticsPlugin)

export default function setupVideoAnalyticsPlugin(player: Player, videoCategory?: string): void {
  // @ts-expect-error: videoAnalytics is a plugin method added at runtime
  player.videoAnalytics(videoCategory)
}
